/* eslint-disable react/jsx-max-depth */
import {
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  VStack,
  Switch,
  Text as ChakraText,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { useInView } from 'react-intersection-observer'
import _ from 'lodash'
import type { ReactElement } from 'react'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import type {
  Notification,
  ListNotificationsResponse,
  MarkNotificationStatusSuccessResponse,
} from '../../../interfaces'
import useToast from '../../../hooks/useToast'
import NoNotifications from '../../../../graphics/NoNotifications'
import NotificationElement from './NotificationElement'

interface Props {
  isOpen: boolean
  onClose(): void
}

// Component to observe each notification
const NotificationObserver = ({
  notification,
  children,
  seen,
  getMoreNotifications,
  changeSeenStatus,
}: {
  notification: Notification
  seen: boolean
  children: React.ReactNode
  getMoreNotifications(notificationId: string): void
  changeSeenStatus(notificationId: string): void
}): ReactElement => {
  const [ref, inView] = useInView({
    threshold: 0.5, // 50% should be visible
    triggerOnce: true, // Trigger only once
  })
  useEffect(() => {
    if (inView) {
      if (!seen) {
        void changeSeenStatus(notification.notificationId)
      }
      void getMoreNotifications(notification.notificationId)
    }
  }, [inView, notification.notificationId])

  return (
    <Flex
      ref={ref}
      key={notification.notificationId}
      borderBottom="1px solid rgba(0, 0, 0, .1)"
      width="100%"
    >
      {children}
    </Flex>
  )
}
interface ReadMapRecordValue {
  new: boolean
  original: boolean
}

const NotificationDrawer = ({
  isOpen,
  onClose,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { user } = useContext(UserContext)
  const [, dispatch] = useContext(MLContext)
  const toast = useToast()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [onlyShowMentioned, setOnlyShowMentioned] = useState(false)
  const [onlyShowUnread, setOnlyShowUnread] = useState(false)
  const [seenNotificationIds, setSeenNotificationIds] = useState<string[]>([])
  const seenNotificationIdsRef = useRef(seenNotificationIds)
  const [markAllAsRead, setMarkAllAsRead] = useState(false)
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [hasNext, setHasNext] = useState<boolean>(true)
  const [readStatusMap, setReadStatusMap] = useState<
    Record<string, ReadMapRecordValue>
  >({})

  const changeReadStatus = (readStatus: boolean, notificationId: string) => {
    setReadStatusMap(prevMap => ({
      ...prevMap,
      [notificationId]: {
        new: readStatus,
        original: prevMap[notificationId].original,
      },
    }))
  }
  const getNotifications = async (): Promise<void> => {
    const baseUrl = `/api/media-library/${user?.clientNumber}/notifications`
    const params: Record<string, string> = {
      limit: '10',
    }

    if (cursor) {
      params.cursor = cursor
    }

    if (onlyShowMentioned) {
      params.mentioned = 'true'
    }

    if (onlyShowUnread) {
      params.read = 'false'
    }

    const searchParams = new URLSearchParams(params)

    const endpoint = `${baseUrl}?${searchParams.toString()}`

    const res = (await request.get(endpoint)) as ListNotificationsResponse

    if (request.error) {
      toast({
        description: t(
          'get_notifications_failed_description',
          'An error occured in fetching notifications.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (res) {
      res.notifications.forEach(notification => {
        setReadStatusMap(prevMap => ({
          ...prevMap,
          [notification.notificationId]: {
            new: notification.read,
            original: notification.read,
          },
        }))
      })
      setNotifications([...notifications, ...res.notifications])

      if (res.cursor) {
        setCursor(res.cursor)
      }
      setHasNext(res.hasNext)
    }
  }

  const getMoreNotifications = async (
    notificationId: string,
  ): Promise<void> => {
    if (cursor && notificationId === cursor && hasNext) {
      void getNotifications()
    }
  }
  const changeSeenStatus = (notificationId: string): void => {
    if (!seenNotificationIds.includes(notificationId)) {
      setSeenNotificationIds(
        _.xor([...seenNotificationIds, notificationId], []),
      )
    }
  }

  const getUnseenNotificationsCount = async (): Promise<void> => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/notifications/count/unseen`,
    )) as { count: number }

    if (request.error) {
      dispatch([
        {
          type: MediaLibraryActions.SET_UNSEEN_NOTIFICATIONS_COUNT,
          payload: 0,
        },
      ])
      toast({
        description: t(
          'unseen_notifications_count_failed_description',
          'An error occured in unseen notifications count.',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else if (res) {
      dispatch([
        {
          type: MediaLibraryActions.SET_UNSEEN_NOTIFICATIONS_COUNT,
          payload: res.count,
        },
      ])
    }
  }

  const markNotificationsAsSeen = async (): Promise<void> => {
    if (user?.clientNumber && seenNotificationIdsRef.current.length > 0) {
      const res = (await request.put(
        `/api/media-library/${user?.clientNumber}/notifications/mark`,
        {
          notificationIds: seenNotificationIdsRef.current,
          mark: 'seen',
        },
      )) as MarkNotificationStatusSuccessResponse

      if (request.error) {
        toast({
          description: t(
            'mark_notifications_failed_description',
            'An error occured in marking notifications.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setSeenNotificationIds([])
        void getUnseenNotificationsCount()
      }
    }
  }

  const setReadStatus = async (
    mark: string,
    allNotifications?: boolean,
  ): Promise<void> => {
    const notificationIdsToBeMarked: string[] = []
    if (mark === 'read') {
      Object.keys(readStatusMap).forEach(item => {
        if (
          readStatusMap[item].new !== readStatusMap[item].original &&
          readStatusMap[item].new
        ) {
          notificationIdsToBeMarked.push(item)
        }
      })
    } else if (mark === 'unread') {
      Object.keys(readStatusMap).forEach(item => {
        if (
          readStatusMap[item].new !== readStatusMap[item].original &&
          !readStatusMap[item].new
        ) {
          notificationIdsToBeMarked.push(item)
        }
      })
    }

    if (notificationIdsToBeMarked.length > 0 || allNotifications) {
      const res = (await request.put(
        `/api/media-library/${user?.clientNumber}/notifications/mark`,
        {
          notificationIds: allNotifications ? 'all' : notificationIdsToBeMarked,
          mark,
        },
      )) as MarkNotificationStatusSuccessResponse

      if (request.error) {
        toast({
          description: t(
            'mark_notifications_failed_description',
            'An error occured in marking notifications.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else if (allNotifications) {
        const updatedReadStatusMap = Object.keys(readStatusMap).reduce(
          (acc, item) => ({
            ...acc,
            [item]: {
              new: mark === 'read',
              original: mark === 'read',
            },
          }),
          {},
        )
        setReadStatusMap(updatedReadStatusMap)
      } else if (res) {
        res.notificationIds.forEach(notificationId => {
          setReadStatusMap(prevMap => ({
            ...prevMap,
            [notificationId]: {
              new: mark === 'read',
              original: mark === 'read',
            },
          }))
        })
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      void getNotifications()
    }
  }, [onlyShowMentioned, onlyShowUnread, isOpen])

  useEffect(() => {
    if (onlyShowUnread) {
      setNotifications(prevValue => [
        ...prevValue.filter(
          notification => !readStatusMap[notification.notificationId].new,
        ),
      ])
    }
    if (!markAllAsRead) {
      void setReadStatus('read', false)
      void setReadStatus('unread', false)
    } else {
      void setReadStatus('read', true)
      setMarkAllAsRead(false)
    }
  }, [readStatusMap, onlyShowUnread])

  // Update ref whenever seenNotificationIds changes
  useEffect(() => {
    seenNotificationIdsRef.current = seenNotificationIds
  }, [seenNotificationIds])

  useEffect(
    () => () => {
      void markNotificationsAsSeen()
    },
    [],
  )

  return (
    <Drawer
      isOpen={isOpen}
      size="md"
      onClose={() => {
        void markNotificationsAsSeen()
        setNotifications([])
        setCursor(undefined)
        setOnlyShowMentioned(false)
        setOnlyShowUnread(false)
        onClose()
      }}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{t('notification', 'Notifications')}</DrawerHeader>

        <DrawerBody p={0}>
          <Tabs
            onChange={index => {
              setOnlyShowMentioned(index === 1)
              setNotifications([])
              setCursor(undefined)
            }}
            display="flex"
            flexDirection="column"
            minHeight="100%"
            height="100%"
            size="xl"
          >
            <TabList>
              <Tab flexGrow={1} width="100%">
                {t('all', 'All')}
              </Tab>
              <Tab flexGrow={1} width="100%">
                {t('only_mentions', 'Only mentions')}
              </Tab>
            </TabList>

            <TabPanels minHeight="0" display="flex" flexGrow={1}>
              <TabPanel p={0} width="100%" overflow="auto">
                <VStack spacing={0} width="100%" height="100%" overflow="auto">
                  <HStack p={4} w="100%" justifyContent="space-between">
                    <Switch
                      isChecked={onlyShowUnread}
                      onChange={e => {
                        void setOnlyShowUnread(e.target.checked)
                        setNotifications([])
                        setCursor(undefined)
                      }}
                    >
                      {t('only_unread', 'Only show unread')}
                    </Switch>
                    <Button
                      variant="link"
                      color="#2c93fa"
                      visibility={
                        Object.keys(readStatusMap).every(
                          item => readStatusMap[item].new,
                        )
                          ? 'hidden'
                          : 'visible'
                      }
                      onClick={() => {
                        setMarkAllAsRead(true)
                        const updatedReadStatusMap = Object.keys(
                          readStatusMap,
                        ).reduce(
                          (acc, item) => ({
                            ...acc,
                            [item]: {
                              new: true,
                              original: readStatusMap[item].original,
                            },
                          }),
                          {},
                        )
                        setReadStatusMap(updatedReadStatusMap)
                      }}
                    >
                      {t('mark_all_as_read', 'Mark all as read')}
                    </Button>
                  </HStack>
                  {notifications.map(notification => (
                    <NotificationObserver
                      key={notification.notificationId}
                      notification={notification}
                      seen={seenNotificationIds.includes(
                        notification.notificationId,
                      )}
                      getMoreNotifications={getMoreNotifications}
                      changeSeenStatus={changeSeenStatus}
                    >
                      <NotificationElement
                        key={notification.notificationId}
                        notification={notification}
                        readStatus={
                          readStatusMap[notification.notificationId].new
                        }
                        changeReadStatus={changeReadStatus}
                      />
                    </NotificationObserver>
                  ))}
                  {loading && (
                    <Center width="full" height="100%">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                        size="xl"
                      />
                    </Center>
                  )}
                  {!loading && notifications.length === 0 && (
                    <Flex
                      flexDirection="column"
                      textAlign="center"
                      justifyContent="center"
                      height="100%"
                      width="100%"
                    >
                      <NoNotifications
                        height="163px"
                        width="237px"
                        margin="0 auto"
                        marginBottom="1em"
                      />
                      <ChakraText
                        color="appBattleshipGrey"
                        fontWeight="500"
                        fontSize="1em"
                        margin="0 auto"
                      >
                        {onlyShowUnread
                          ? t(
                              'no_unread_notification_past_30_days',
                              'You have no unread notifications.',
                            )
                          : t(
                              'no_notification_past_30_days',
                              'You have no notifications for past 30 days.',
                            )}
                      </ChakraText>
                    </Flex>
                  )}
                </VStack>
              </TabPanel>
              <TabPanel p={0} width="100%" overflow="auto">
                <VStack spacing={0} height="100%" width="100%">
                  <HStack p={4} w="100%" justifyContent="space-between">
                    <Switch
                      isChecked={onlyShowUnread}
                      onChange={e => {
                        void setOnlyShowUnread(e.target.checked)
                        setNotifications([])
                        setCursor(undefined)
                      }}
                    >
                      {t('only_unread', 'Only show unread')}
                    </Switch>
                    <Button
                      variant="link"
                      color="#2c93fa"
                      visibility={
                        Object.keys(readStatusMap).every(
                          item => readStatusMap[item].new,
                        )
                          ? 'hidden'
                          : 'visible'
                      }
                      onClick={() => {
                        setMarkAllAsRead(true)
                        const updatedReadStatusMap = Object.keys(
                          readStatusMap,
                        ).reduce(
                          (acc, item) => ({
                            ...acc,
                            [item]: {
                              new: true,
                              original: readStatusMap[item].original,
                            },
                          }),
                          {},
                        )
                        setReadStatusMap(updatedReadStatusMap)
                      }}
                    >
                      {t('mark_all_as_read', 'Mark all as read')}
                    </Button>
                  </HStack>
                  {notifications.map(notification => (
                    <NotificationObserver
                      key={notification.notificationId}
                      notification={notification}
                      seen={seenNotificationIds.includes(
                        notification.notificationId,
                      )}
                      getMoreNotifications={getMoreNotifications}
                      changeSeenStatus={changeSeenStatus}
                    >
                      <NotificationElement
                        key={notification.notificationId}
                        notification={notification}
                        readStatus={
                          readStatusMap[notification.notificationId].new
                        }
                        changeReadStatus={changeReadStatus}
                      />
                    </NotificationObserver>
                  ))}
                  {!loading && notifications.length === 0 && (
                    <Flex
                      flexDirection="column"
                      textAlign="center"
                      justifyContent="center"
                      height="100%"
                      width="100%"
                    >
                      <NoNotifications
                        height="163px"
                        width="237px"
                        margin="0 auto"
                        marginBottom="1em"
                      />
                      <ChakraText
                        color="appBattleshipGrey"
                        fontWeight="500"
                        fontSize="1em"
                        margin="0 auto"
                      >
                        {onlyShowUnread
                          ? t(
                              'no_unread_notification_past_30_days',
                              'You have no unread notifications.',
                            )
                          : t(
                              'no_notification_past_30_days',
                              'You have no notifications for past 30 days.',
                            )}
                      </ChakraText>
                    </Flex>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default NotificationDrawer
